import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react"
import {
  AccessCodeForgottenDialog,
  type AccessCodeForgottenDialogProps,
} from "./AccessCodeForgottenDialog"
import {
  ActivatePrivateAccessDialog,
  type ActivatePrivateAccessDialogProps,
} from "./ActivatePrivateAccessDialog"
import { EmailSentDialog, type EmailSentDialogProps } from "./EmailSentDialog"
import { LoadingDialog, type LoadingDialogProps } from "./LoadingDialog"
import { PinDialog, type PinDialogProps } from "./PinDialog"

type PinDialogConfiguration = Omit<PinDialogProps, "open" | "closeDialog">
type EmailSentConfiguration = Omit<EmailSentDialogProps, "open" | "closeDialog">
type AccessCodeForgottenConfiguration = Omit<AccessCodeForgottenDialogProps, "open">
type ActivatePrivateAccessConfiguration = Omit<
  ActivatePrivateAccessDialogProps,
  "open" | "closeDialog"
>
type LoadingConfiguration = Omit<LoadingDialogProps, "open">
interface SecurityDialogContextValue {
  openPinDialog: (conf: PinDialogConfiguration) => void
  openEmailSentDialog: (conf: EmailSentConfiguration) => void
  openAccessCodeForgottenDialog: (conf: AccessCodeForgottenConfiguration) => void
  openActivatePrivateAccessDialog: (conf: ActivatePrivateAccessConfiguration) => void
  openLoadingDialog: (conf: LoadingConfiguration) => void
  closeDialog: () => void
}

const SecurityDialogContext = createContext<SecurityDialogContextValue>(undefined!)

export const SecurityDialogProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(dialogsReducer, defaultDialogState)
  const closeDialog = useCallback(() => dispatch({ type: "CLOSE" }), [])
  const openPinDialog = useCallback(
    (payload: PinDialogConfiguration) => dispatch({ type: "OPEN_PIN", payload }),
    [],
  )
  const openEmailSentDialog = useCallback(
    (payload: EmailSentConfiguration) =>
      dispatch({ type: "OPEN_EMAIL_SENT", payload }),
    [],
  )
  const openAccessCodeForgottenDialog = useCallback(
    (payload: AccessCodeForgottenConfiguration) =>
      dispatch({ type: "OPEN_ACCESS_CODE_FORGOTTEN", payload }),
    [],
  )
  const openActivatePrivateAccessDialog = useCallback(
    (payload: ActivatePrivateAccessConfiguration) =>
      dispatch({ type: "OPEN_ACTIVATE_PRIVATE_ACCESS", payload }),
    [],
  )
  const openLoadingDialog = useCallback(
    (payload: LoadingConfiguration) => dispatch({ type: "OPEN_LOADING", payload }),
    [],
  )

  return (
    <SecurityDialogContext.Provider
      value={{
        openPinDialog,
        openEmailSentDialog,
        openAccessCodeForgottenDialog,
        openActivatePrivateAccessDialog,
        openLoadingDialog,
        closeDialog,
      }}
    >
      {children}
      {state.dialogOpened === "pin" && (
        <PinDialog {...state.dialogConf} open closeDialog={closeDialog} />
      )}
      {state.dialogOpened === "emailSent" && (
        <EmailSentDialog {...state.dialogConf} open closeDialog={closeDialog} />
      )}
      {state.dialogOpened === "accessCodeForgotten" && (
        <AccessCodeForgottenDialog {...state.dialogConf} open />
      )}
      {state.dialogOpened === "activatePrivateAccess" && (
        <ActivatePrivateAccessDialog {...state.dialogConf} open />
      )}
      {state.dialogOpened === "loading" && (
        <LoadingDialog {...state.dialogConf} open />
      )}
    </SecurityDialogContext.Provider>
  )
}

export const useSecurityDialog = () => {
  const ctx = useContext(SecurityDialogContext) ?? {}
  return useMemo(
    () => ({
      pinDialog: (openProps: PinDialogConfiguration) => ctx.openPinDialog(openProps),
      emailSentDialog: (openProps: EmailSentConfiguration) =>
        ctx.openEmailSentDialog(openProps),
      accessCodeForgottenDialog: (openProps: AccessCodeForgottenConfiguration) =>
        ctx.openAccessCodeForgottenDialog(openProps),
      activatePrivateAccessDialog: (openProps: ActivatePrivateAccessConfiguration) =>
        ctx.openActivatePrivateAccessDialog(openProps),
      loadingDialog: (openProps: LoadingConfiguration) =>
        ctx.openLoadingDialog(openProps),
      closeDialog: ctx.closeDialog,
    }),
    [ctx],
  )
}

type SecurityDialogState =
  | { dialogOpened: "pin"; dialogConf: PinDialogConfiguration }
  | { dialogOpened: "emailSent"; dialogConf: EmailSentConfiguration }
  | {
      dialogOpened: "accessCodeForgotten"
      dialogConf: AccessCodeForgottenConfiguration
    }
  | {
      dialogOpened: "activatePrivateAccess"
      dialogConf: ActivatePrivateAccessConfiguration
    }
  | { dialogOpened: "loading"; dialogConf: LoadingConfiguration }
  | { dialogOpened: null; dialogConf: null }

const defaultDialogState: SecurityDialogState = {
  dialogOpened: null,
  dialogConf: null,
}

type SecurityDialogAction =
  | { type: "OPEN_PIN"; payload: PinDialogConfiguration }
  | { type: "OPEN_EMAIL_SENT"; payload: EmailSentConfiguration }
  | { type: "OPEN_ACCESS_CODE_FORGOTTEN"; payload: AccessCodeForgottenConfiguration }
  | {
      type: "OPEN_ACTIVATE_PRIVATE_ACCESS"
      payload: ActivatePrivateAccessConfiguration
    }
  | { type: "OPEN_LOADING"; payload: LoadingConfiguration }
  | { type: "CLOSE"; payload?: undefined }

function dialogsReducer(
  _state: SecurityDialogState,
  action: SecurityDialogAction,
): SecurityDialogState {
  const { type, payload } = action
  switch (type) {
    case "OPEN_PIN":
      return { dialogOpened: "pin", dialogConf: payload }
    case "OPEN_EMAIL_SENT":
      return { dialogOpened: "emailSent", dialogConf: payload }
    case "OPEN_ACCESS_CODE_FORGOTTEN":
      return { dialogOpened: "accessCodeForgotten", dialogConf: payload }
    case "OPEN_ACTIVATE_PRIVATE_ACCESS":
      return { dialogOpened: "activatePrivateAccess", dialogConf: payload }
    case "OPEN_LOADING":
      return { dialogOpened: "loading", dialogConf: payload }
    case "CLOSE":
      return defaultDialogState
  }
}
